import { Package } from 'lucide-react';
import { RowListSkeleton } from '../../../../../shared/components/RowListSkeleton';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import type { ContainerSize, ContainerType, JobType, SpecialHandlingRequirement } from '../../../../reference-data/reference-data.api';
import { ContainerDetailsSection } from './ContainerDetailsSection';
import { LooseCargoSection } from './LooseCargoSection';
import type { JobDetail } from '../../../jobs.api';
import type { JobCataloguesState } from '../../../hooks/use-job-catalogues';

interface JobCargoTabProps {
  job: JobDetail;
  catalogues: JobCataloguesState;
  jobTypes: JobType[];
  containerSizes: ContainerSize[];
  containerTypes: ContainerType[];
  specialHandlingRequirements: SpecialHandlingRequirement[];
  onChanged: () => Promise<void>;
}

/**
 * Container and loose-cargo jobs show their own cargo model (gitaumoses4/chukua#31, #32); other
 * families have none yet. The family is read off `jobTypes`, which loads with the rest of the
 * catalogues — a skeleton covers that wait, rather than flashing "No cargo details" while it's
 * still `[]`.
 */
export function JobCargoTab({ job, catalogues, jobTypes, containerSizes, containerTypes, specialHandlingRequirements, onChanged }: JobCargoTabProps) {
  if (catalogues.status === 'loading') return <RowListSkeleton label="Loading cargo" />;

  const family = jobTypes.find((t) => t.code === job.jobTypeCode)?.family;
  if (family === 'container') {
    return <ContainerDetailsSection job={job} jobTypes={jobTypes} containerSizes={containerSizes} containerTypes={containerTypes} onChanged={onChanged} />;
  }
  if (family === 'loose') {
    return <LooseCargoSection job={job} jobTypes={jobTypes} specialHandlingRequirements={specialHandlingRequirements} onChanged={onChanged} />;
  }
  return (
    <EmptyState
      icon={Package}
      title="No cargo details for this job type"
      description="Cargo detail is captured for container and loose-cargo jobs only so far."
    />
  );
}
